import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiScissors, FiStar, FiHeart, FiAward, FiUsers, FiCalendar } = FiIcons;

const Landing = () => {
  const services = [
    {
      icon: FiScissors,
      title: "Custom Styling & Cuts",
      description: "Personalized to enhance your natural beauty"
    },
    {
      icon: FiStar,
      title: "Coloring & Highlights",
      description: "Radiant colors that shine bright"
    },
    {
      icon: FiHeart,
      title: "Special Occasion Glam",
      description: "Get ready to dazzle on your big day"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      rating: 5,
      text: "Melissa transformed my hair completely! The attention to detail and personalized service is outstanding."
    },
    {
      name: "Emily Davis",
      rating: 5,
      text: "Best salon experience I've ever had. The atmosphere is so relaxing and the results are always perfect."
    },
    {
      name: "Jessica Wilson",
      rating: 5,
      text: "I've been coming here for years and they never disappoint. Highly recommend for any special occasion!"
    }
  ];

  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="hero-bg py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Welcome to <span className="text-gradient">Melissa's</span><br />
                Hairdressing Salon
              </h1>
              <p className="text-xl text-gray-600 mb-4 font-medium">
                Experience the Magic of Personalized Beauty
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                At Melissa's Hairdressing Salon, we're here to inspire and transform you with a touch of elegance and style. Whether it's a casual refresh or a special event makeover, our dedicated team of stylists is committed to bringing your dream look to life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact-form"
                  className="btn-primary text-white px-8 py-4 rounded-full font-medium text-lg text-center"
                >
                  Book Your Appointment Now
                </Link>
                <Link 
                  to="/services"
                  className="border-2 border-amber-600 text-amber-600 px-8 py-4 rounded-full font-medium text-lg text-center hover:bg-amber-600 hover:text-white transition-all"
                >
                  View Services
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Beautiful hair styling at Melissa's Salon"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiUsers} className="text-2xl text-amber-600" />
                  <div>
                    <p className="font-bold text-lg">500+</p>
                    <p className="text-gray-600 text-sm">Happy Clients</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Highlight */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Discover Your Perfect Look
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              At Melissa's Hairdressing Salon, every style is a personal journey, and we're delighted to walk it with you. Our services are tailored to meet the individual needs of each guest, making sure you leave feeling beautiful and satisfied.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="service-card p-8 rounded-2xl card-hover text-center"
              >
                <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <SafeIcon icon={service.icon} className="text-white text-2xl" />
                </div>
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mt-12"
          >
            <p className="text-lg text-gray-600 mb-6">
              Find your new favorite style today.
            </p>
            <Link 
              to="/contact-form"
              className="btn-primary text-white px-8 py-3 rounded-full font-medium text-lg"
            >
              Schedule a Free Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-amber-50 to-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: FiUsers, number: "500+", label: "Happy Clients" },
              { icon: FiAward, number: "10+", label: "Years Experience" },
              { icon: FiStar, number: "5.0", label: "Average Rating" },
              { icon: FiCalendar, number: "1000+", label: "Appointments" }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <SafeIcon icon={stat.icon} className="text-3xl text-amber-600 mx-auto mb-3" />
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </h3>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="testimonial-card p-8 rounded-2xl card-hover"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic leading-relaxed">
                  "{testimonial.text}"
                </p>
                <p className="font-semibold text-gray-900">
                  {testimonial.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Look?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Book your appointment today and experience the magic of personalized beauty at Melissa's Wonderful Hair Salon.
            </p>
            <Link 
              to="/contact-form"
              className="bg-white text-amber-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all inline-block"
            >
              Book Your Appointment
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Landing;